import { getLearningWordsDictionary, searchWords } from "@/api/Api";
import { getKnownWordsDictionary } from "@/api/Api";
import { callWithAuth } from "@/api/Api";
import { UserWord } from "@shared_types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";

type UseUserWordsProps = {
  viewMode: "known" | "learning";
  searchQuery: string;
  pageSize: number;
};

// Number of items to render initially, regardless of how many are in cache
const INITIAL_DISPLAY_LIMIT = 30;

export const useUserWords = ({
  viewMode,
  searchQuery,
  pageSize,
}: UseUserWordsProps) => {
  // Track how many items should be displayed
  const [displayLimit, setDisplayLimit] = useState(INITIAL_DISPLAY_LIMIT);

  // Reset the display limit when switching views or changing search queries
  useEffect(() => {
    setDisplayLimit(INITIAL_DISPLAY_LIMIT);
  }, [viewMode, searchQuery]);

  // Fetch known words with pagination
  const knownWordsQuery = useInfiniteQuery({
    queryKey: ["knownWords"],
    queryFn: ({ pageParam = 1 }) => {
      return callWithAuth(getKnownWordsDictionary, {
        page: pageParam,
        limit: pageSize,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextPage !== 0 ? lastPage.nextPage : undefined,
    initialPageParam: 1,
  });

  // Fetch learning words with pagination
  const learningWordsQuery = useInfiniteQuery({
    queryKey: ["learningWords"],
    queryFn: ({ pageParam = 1 }) => {
      return callWithAuth(getLearningWordsDictionary, {
        page: pageParam,
        limit: pageSize,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextPage !== 0 ? lastPage.nextPage : undefined,
    initialPageParam: 1,
  });

  // Search query for server-side search
  const searchWordsQuery = useInfiniteQuery({
    queryKey: ["searchWords", viewMode, searchQuery],
    queryFn: ({ pageParam = 1 }) => {
      return callWithAuth(searchWords, {
        status: viewMode,
        query: searchQuery,
        page: pageParam,
        limit: pageSize,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextPage !== 0 ? lastPage.nextPage : undefined,
    initialPageParam: 1,
    enabled: searchQuery.length > 0,
  });

  // Get all known words from query data
  const knownWords =
    knownWordsQuery.data?.pages.flatMap((page) => page.knownWords) || [];
  // Get all learning words from query data
  const learningWords =
    learningWordsQuery.data?.pages.flatMap((page) => page.learningWords) || [];
  // Get search results
  const serverSearchResult =
    searchWordsQuery.data?.pages.flatMap((page) => page.words) || [];

  // Determine active view data
  let allActiveViewWords: UserWord[] = [];
  if (searchQuery.length > 0) {
    allActiveViewWords = serverSearchResult;
  } else if (viewMode === "known") {
    allActiveViewWords = knownWords;
  } else if (viewMode === "learning") {
    allActiveViewWords = learningWords;
  } else {
    console.error("Invalid view mode");
    allActiveViewWords = [];
  }

  // Limit the number of items actually returned for rendering
  const activeViewWords = allActiveViewWords.slice(0, displayLimit);

  // Determine if we have more items to show
  const hasMoreToShow = displayLimit < allActiveViewWords.length;

  // Get counts. While searching, the active provider's totalCount comes from the
  // search response (iOS useWordsDataProvider.totalCount switches to the search
  // query's totalCount when isSearching), so the active side of the toggle shows
  // the number of matches while the inactive side keeps its base count.
  const baseKnownCount = knownWordsQuery.data?.pages[0]?.totalCount || 0;
  const baseLearningCount = learningWordsQuery.data?.pages[0]?.totalCount || 0;
  const searchTotalCount = searchWordsQuery.data?.pages[0]?.totalCount || 0;
  const isSearching = searchQuery.length > 0;
  const knownCount =
    isSearching && viewMode === "known" ? searchTotalCount : baseKnownCount;
  const learningCount =
    isSearching && viewMode === "learning"
      ? searchTotalCount
      : baseLearningCount;

  // Determine active query based on view mode and search state
  const activeQuery =
    searchQuery.length > 0
      ? searchWordsQuery
      : viewMode === "known"
      ? knownWordsQuery
      : learningWordsQuery;

  // Function to load more items from cache or fetch more if needed
  const loadMore = () => {
    if (displayLimit < allActiveViewWords.length) {
      // We have more cached items, just increase the display limit
      setDisplayLimit((prev) => prev + INITIAL_DISPLAY_LIMIT);
    } else if (activeQuery.hasNextPage && !activeQuery.isFetchingNextPage) {
      // We need to fetch more from the server
      activeQuery.fetchNextPage().then(() => {
        // After fetching, increase display limit to show new items
        setDisplayLimit((prev) => prev + INITIAL_DISPLAY_LIMIT);
      });
    }
  };

  return {
    activeQuery,
    activeViewWords,
    knownCount,
    learningCount,
    fetchNextPage: loadMore,
    totalItemsAvailable: allActiveViewWords.length,
    refetchAllQueries: () => {
      knownWordsQuery.refetch();
      learningWordsQuery.refetch();
      searchWordsQuery.refetch();
      setDisplayLimit(INITIAL_DISPLAY_LIMIT);
    },
    isInitialLoading: activeQuery.isLoading,
    isLoadingMore:
      activeQuery.isFetchingNextPage ||
      (hasMoreToShow && displayLimit < allActiveViewWords.length),
  };
};
